import { formatMoney, GAME_DEFINITIONS, SIZE_SPECS, roleCan, sizePricePerHour, type ServerAction, type ServerSize, type ServerView } from '@hearth/shared';
import { ApiError } from '../api';
import type { CampfireState } from '../ui/Campfire';

// The servers page's words and rules, kept apart from React so they're simple to test. The UI says
// Asleep, Wake and Sleep (never stopped, start, stop), and never shows a player limit.

/** What a card offers: its address and Sleep, a note while it changes, or Wake. */
export type CardKind = 'running' | 'changing' | 'asleep';

export function cardKind(s: ServerView): CardKind {
  if (s.status === 'RUNNING') return 'running';
  if (['STOPPED', 'FAILED', 'ARCHIVED', 'DESTROYED'].includes(s.status)) return 'asleep';
  return 'changing';
}

export function campfireFor(s: ServerView): CampfireState {
  if (s.status === 'RUNNING') return 'lit';
  if (s.status === 'PROVISIONING' || s.status === 'STARTING' || s.status === 'RESTORING') return 'catching';
  return cardKind(s) === 'changing' ? 'fading' : 'embers';
}

/**
 * A server being set up again (a new instance) after it has run before: its world comes back from
 * a backup, so the wake is slower. People are only told it may take longer (owner, M12).
 */
export const slowWake = (s: ServerView) => s.status === 'PROVISIONING' && s.lastStartedAt !== undefined;

/** The status line under a server's name. */
export function statusText(s: ServerView, now: Date): string {
  switch (s.status) {
    case 'RUNNING':
      if (s.playersOnline === undefined) return 'Running';
      return s.playersOnline === 0 ? 'Running · nobody on' : `Running · ${s.playersOnline} playing`;
    case 'PROVISIONING':
    case 'STARTING':
    case 'RESTORING':
      return slowWake(s) ? 'Waking up… This may take longer than normal.' : 'Waking up…';
    case 'FAILED':
      return 'Something went wrong';
    default:
      if (cardKind(s) === 'changing') return 'Falling asleep…';
      return s.lastPlayedAt ? `Asleep · played ${timeAgo(s.lastPlayedAt, now)}` : 'Asleep';
  }
}

/** What fills the address's place while a server isn't running. */
export function slotText(s: ServerView): string {
  if (s.status === 'RUNNING') return 'The address is on its way…';
  if (s.status === 'FAILED') return 'Try waking it again';
  if (cardKind(s) === 'asleep') return 'Wake it to get an address';
  if (slowWake(s)) return 'This may take longer than normal';
  if (campfireFor(s) !== 'catching') return 'Saving and backing up…';
  return s.hostname ? 'Ready in about a minute…' : 'Address in about a minute…';
}

/**
 * Where players connect, if there's somewhere to show: its own name (domain plan D4), which never
 * changes, so whatever it's doing; else (an env without a domain) its IP while it runs. None after a
 * failure or once it's destroyed.
 */
export function joinAddress(s: ServerView): string | undefined {
  if (s.status === 'FAILED' || s.status === 'DESTROYED') return undefined;
  return s.hostname ?? (s.status === 'RUNNING' ? s.address : undefined);
}

/** The status line's colour: running, waking (catching), falling asleep (fading) or asleep. */
export function statusTone(s: ServerView): string {
  const kind = cardKind(s);
  return kind === 'changing' ? campfireFor(s) : kind;
}

/** On a server's page, in the address's place while it isn't running (with a name: under it, while it changes). */
export function joinNote(s: ServerView): string {
  if (s.hostname && cardKind(s) === 'changing') {
    if (campfireFor(s) !== 'catching') return 'Falling asleep.';
    return slowWake(s) ? 'Waking up. This may take longer than normal.' : 'Waking up. Join here in about a minute.';
  }
  if (s.status === 'RUNNING') return 'The address is on its way…';
  if (s.status === 'FAILED') return 'Something went wrong. Try waking it again.';
  if (cardKind(s) === 'asleep') return "It's asleep. Wake it and the address shows up here.";
  if (slowWake(s)) return "It's waking up. This may take longer than normal: the address shows up here when it's ready.";
  return campfireFor(s) === 'catching' ? "It's waking up: the address shows up here in about a minute." : "It's falling asleep.";
}

/** How players join, under the address. */
export function howToJoin(s: ServerView): string {
  if (!s.hostname) return `In ${GAME_DEFINITIONS[s.game].shortName} ${s.version}: Multiplayer → Direct Connect (or Add Server).`;
  if (s.status === 'RUNNING') {
    return `In ${GAME_DEFINITIONS[s.game].shortName} ${s.version}: Multiplayer → Add Server, paste it, and join. It stays the same every time it wakes.`;
  }
  return "It's asleep. Wake it, then join here. The address never changes, so save it in Minecraft once.";
}

/** "$0.13 an hour": what a size costs while awake (M11; the price billed, shown to the cent). */
export const rateText = (size: ServerSize) => `${formatMoney(sizePricePerHour(size))} an hour`;

/** "Small · up to 4 players": its size and who it suits (no limit is enforced). */
export function sizeText(size: ServerSize): string {
  const spec = SIZE_SPECS[size];
  return `${spec.name} · ${spec.players.charAt(0).toLowerCase()}${spec.players.slice(1)}`;
}

/** The Details panel: label and value, in order. */
export function detailRows(s: ServerView, now: Date): [string, string][] {
  const rows: [string, string][] = [
    ['Game', `${GAME_DEFINITIONS[s.game].displayName} ${s.version}`],
    ['Size', sizeText(s.size)],
  ];
  if (s.status === 'RUNNING' && s.playersOnline !== undefined) rows.push(['Players', s.playersOnline === 0 ? 'Nobody on' : `${s.playersOnline} playing`]);
  rows.push(
    ['Falls asleep', s.idleStopMinutes === 0 ? 'Never on its own' : `After ${minutes(s.idleStopMinutes)} with nobody on`],
    ['Last played', s.lastPlayedAt ? capitalized(timeAgo(s.lastPlayedAt, now)) : 'Not yet'],
    ['Last backup', s.lastBackupAt ? capitalized(timeAgo(s.lastBackupAt, now)) : 'None yet: one is taken each time it falls asleep'],
    ['You are', s.role === 'owner' ? 'Its owner' : s.role === 'member' ? 'A member' : 'An admin'],
  );
  return rows;
}

function capitalized(text: string): string {
  return text.charAt(0).toUpperCase() + text.slice(1);
}

/** "Minecraft 26.3 · yours". */
export function metaText(s: ServerView): string {
  const whose = s.role === 'owner' ? 'yours' : s.role === 'member' ? "a friend's" : 'as an admin';
  return `${GAME_DEFINITIONS[s.game].shortName} ${s.version} · ${whose}`;
}

/** Whether the caller may do this to the server (admins may do anything). */
export function may(s: ServerView, action: ServerAction): boolean {
  return s.role === 'admin' || roleCan(s.role, action);
}

/** "1 running, 1 on the move, 2 asleep." */
export function summaryText(servers: readonly ServerView[]): string {
  const count = (kind: CardKind) => servers.filter((s) => cardKind(s) === kind).length;
  const parts = [
    [count('running'), 'running'],
    [count('changing'), 'on the move'],
    [count('asleep'), 'asleep'],
  ].filter(([n]) => n) as [number, string][];
  return `${parts.map(([n, what]) => `${n} ${what}`).join(', ')}.`;
}

/** How often to refresh: often while something is changing, gently otherwise. */
export function pollMs(servers: readonly ServerView[]): number {
  return servers.some((s) => cardKind(s) === 'changing') ? 5_000 : 30_000;
}

/** The question before waking or putting a server to sleep. */
export function confirmText(s: ServerView, action: 'wake' | 'sleep'): { title: string; body: string } {
  if (action === 'wake') {
    const sleepsAfter = s.idleStopMinutes === 0 ? '' : `, and falls asleep again once nobody's played for ${minutes(s.idleStopMinutes)}`;
    return { title: `Wake ${s.name}?`, body: `It'll be ready in about a minute${sleepsAfter}.` };
  }
  const on = s.playersOnline ?? 0;
  return {
    title: `Put ${s.name} to sleep?`,
    body:
      on > 0
        ? `${on === 1 ? '1 person is' : `${on} people are`} playing. They'll be disconnected, and the world is saved and backed up first.`
        : "Nobody's on. The world is saved and backed up, and the server stops costing anything.",
  };
}

/** A failed request, in words for people. */
export function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  return "Hearth can't be reached right now. It'll try again in a moment.";
}

/** "just now", "5 minutes ago", "yesterday", "3 weeks ago", or the date. */
export function timeAgo(iso: string, now: Date): string {
  const seconds = Math.max(0, (now.getTime() - new Date(iso).getTime()) / 1000);
  const n = (value: number, unit: string) => `${Math.floor(value)} ${unit}${Math.floor(value) === 1 ? '' : 's'} ago`;
  if (seconds < 60) return 'just now';
  if (seconds < 3600) return n(seconds / 60, 'minute');
  if (seconds < 86_400) return n(seconds / 3600, 'hour');
  if (seconds < 2 * 86_400) return 'yesterday';
  if (seconds < 14 * 86_400) return n(seconds / 86_400, 'day');
  if (seconds < 60 * 86_400) return n(seconds / (7 * 86_400), 'week');
  return `on ${new Date(iso).toLocaleDateString('en', { day: 'numeric', month: 'long', year: 'numeric' })}`;
}

function minutes(m: number): string {
  if (m % 60 === 0) return m === 60 ? 'an hour' : `${m / 60} hours`;
  return `${m} minutes`;
}
